/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isNumber from 'lodash.isnumber';
import isUndefined from 'lodash.isundefined';
import {translate} from '../locale/locale';
import {Results} from '../services/GroupProperties';
import {
  OperatorBulkEditorFormFields
} from '../types/editors';
import {
  AccountLink,
  CTCEntityResponseWrapper,
  Entity,
  Operator
} from '../types/entities';
import {
  BulkOperationResults,
  ExtendedResults
} from '../types/services';
import {
  EntityTemplate,
  TemplateColumn,
  TemplateOperators,
  TemplateValidation
} from '../types/templates';
import {Mapped} from '../utils/ObjectUtil';
import {
  createLookupSheet,
  createValidation,
  formatErrors,
  interpolateValidation,
  statusColumn
} from './TemplateHelpers';

// defines the type definitions for driver template options ...
export type DriverTemplateOptions = {
  operatorTypes?: Results;
  categories?: Results;
  groupName?: string;
};

const operatorTypeColumn = ({validation, options, workbook}): TemplateValidation => {
  const operatorTypes = options?.operatorTypes?.toString() ?? '';
  validation.error    = interpolateValidation(validation, 'operator types');
  if (operatorTypes !== '') {
    validation.formulae = createLookupSheet({
      sheetName    : 'operatorType',
      optionString : operatorTypes,
      workbook
    });
  }
  return validation;
};

const operatorCategoryColumn = ({validation, options, workbook}): TemplateValidation => {
  const categories    = options?.categories?.toString() ?? '';
  validation.error    = interpolateValidation(validation, 'operator categories');
  if (categories !== '') {
    validation.formulae = createLookupSheet({
      sheetName    : 'operatorCategory',
      optionString : categories,
      workbook
    });
  }
  return validation;
};

/**
 * Provides a utility function which manages the dynamic generation of an XLSX
 * spreadsheet specific to Driver imports for download by the User.
 *
 * Driver Template column definitions are defined as localized entries within
 * the admin-drivers.json localization documents for each respective locale.
 * Entries take the following form:
 *
 * ```typescript
 *
 * import {driversTemplate} from '@ion/core/src/xlsx/DriverTemplate';
 *
 * const templates = {
 *   [Entity.operator] : DriverTemplate
 * };
 * ```
 *
 * @param columns An array of localized driver template column definitions.
 * @param options An array of other optional column definitions.
 */
export const driversTemplate = ({columns, workbook, options = {}}: EntityTemplate) => {
  const columnEntries: TemplateColumn[] = [];
  const columnValidations: TemplateValidation[] = [];
  const templateColumns = {
    [OperatorBulkEditorFormFields.Status]           : statusColumn,
    [OperatorBulkEditorFormFields.Type]             : operatorTypeColumn,
    [OperatorBulkEditorFormFields.OperatorCategory] : operatorCategoryColumn
  };
  columns.forEach((column, index) => {
    const {key, type, required, max, min, error, width, style} = column;
    const validation = createValidation({required, error, index, type});
    let {header} = column;
    if (error) {
      if (isNumber(max) && required) {
        validation.formulae = [1, max];
        validation.operator = TemplateOperators.Between;
      } else if (isNumber(max)) {
        validation.formulae = [max];
        validation.operator = TemplateOperators.LessThanOrEqual;
      } else if (isNumber(min)) {
        validation.formulae = [min];
        validation.operator = TemplateOperators.GreaterThanOrEqual;
      }
    }
    if (header.includes('__group__')) {
      header = header.replace('__group__', options?.groupName ? `(${options.groupName})` : '');
    }
    columnEntries.push({key, header, width, style});
    columnValidations.push(templateColumns[key]?.({column, validation, workbook, options}) || validation);
  });
  return {
    columnEntries,
    columnValidations
  };
};

/**
 * Provides a utility function which manages the generation of spreadsheet errors
 * specific to Drivers import for download by the user.
 *
 * ```typescript
 *
 * import {getDriverImportErrors} from '@ion/core/src/xlsx/DriverTemplate';
 *
 * const processedResult = getDriverImportErrors(invalidDrivers, result);
 * ```
 *
 * @param invalidDrivers Drivers which are invalid to be imported.
 * @param result Response returned from server.
 *
 * @returns The processed results with errors if any
 */
export function getDriverImportErrors(
  invalidDrivers: CTCEntityResponseWrapper<Operator>[],
  result: ExtendedResults,
  group: AccountLink
): ExtendedResults {
  if (result?.errors?.length) {
    const formattedErrors = formatErrors(result?.errors, Entity.operator);
    result.errors = [];
    result.errors = [...formattedErrors];
  }
  if (invalidDrivers?.length) {
    invalidDrivers.forEach(({operator}: any) => {
      let message;
      const name        = `${operator.firstName} ${operator.lastName}`;
      const accountName = group?.title;
      if (operator.invalidBadge) {
        message = translate('common:editors.driverEditor.badge.error');
      } else if (operator.groupName || operator.parentGroup) {
        message = translate('admin-drivers:import.errors.invalidGroup', {
          driver : name,
          group  : operator.groupName || operator.parentGroup,
          parent : accountName
        });
      } else if (operator.isScheduleNotFound) {
        message = translate('admin-drivers:import.errors.invalidSchedule', {
          driver   : name,
          schedule : operator.schedule,
          group    : operator.account?.title || ''
        });
      } else if (!operator.groupName) {
        message = translate('admin-drivers:import.errors.noGroup', {
          driver : name
        });
      }
      result?.errors?.push({
        error : message,
        name,
        message,
        operator
      });
    });
  }
  return result;
};

/**
 * Provides a utility function which maps the results against the template columns
 * before the same is available as excel for user to download.
 *
 * ```typescript
 *
 * import {exportMapper} from '@ion/core/src/xlsx/Driveremplate';
 *
 * const processedResult = exportMapper(invalidDrivers, result);
 * ```
 *
 * @param results Response from server.
 * @param boolean Checks if response has errors only.
 * @param Mapped Map which helps in mapping the results against respective
 * template columns.
 *
 * @returns The results mapped against the respective columns for generating
 * excel data.
 */
export function exportMapper(
  {results, errors}: BulkOperationResults | ExtendedResults,
  errorsOnly: boolean,
  attributes: Mapped
): unknown[] {
  const entries      = [];
  const items        = errorsOnly ? errors : results.concat(errors);
  const succeeded    = translate('common:succeeded');
  const failed       = translate('common:failed');
  const unavailable  = translate('common:notAvailable');
  items?.forEach(item => {
    const entry = [];
    const isGlobalError = !isUndefined(item.code);
    const hasErrors = !isUndefined(item.error) || isGlobalError;
    const result    = hasErrors ? item.operator : item;
    Object.values(attributes)?.forEach((attribute: string) => {
      if (!isGlobalError || item.operator) {
        const val = result[attribute];
        switch (attribute) {
          case 'groupName':
            entry.push(result.account ? result.account.title : result.groupName);
            break;
          case 'asset':
            entry.push(result.asset ? result.asset.title || unavailable : unavailable);
            break;
          case 'operatorAlias':
            entry.push(result.externalId);
            break;
          case 'schedule':
            entry.push(result.schedules?.length ? result.schedules[0].title : '');
            break;
          default :
            entry.push(val);
            break;
        }
      } else {
        entry.push(unavailable);
      }
    });
    entry.push(hasErrors ? failed : succeeded);
    entry.push(hasErrors ? (!isGlobalError ? item.error : item.message) : '');
    entries.push(entry);
  });
  return entries;
};
